import { SmartusComponentsModule } from '@affinis/smartus-components';
import { CardConfig } from '@affinis/smartus-components/lib/card/card.component.types';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AddressViewModel,
  BaseSelectViewModel,
  LocationClient,
  RoomViewModel,
  UpdateSiteRequest,
} from '@core/api';
import { RoutePath } from '@core/routing/routing.types';
import { BreadcrumbService } from '@core/services/breadcrumb-service/BreadcrumbService';
import { UserService } from '@core/services/oauth-service/user.service';
import { SessionStorageService } from '@core/services/session-storage-service/session-storage.service';
import { MtxGridColumn, MtxGridModule } from '@ng-matero/extensions/grid';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OverrideModule } from '@override/override.module';
import { DialogComponent } from '@shared/components/dialog/dialog.component';
import { UserRoles } from '@shared/constants/user-roles.constants';
import { ToastrService } from 'ngx-toastr';
import {
  catchError,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { SafeHtmlPipe } from '../../../../app/shared/pipes/safe-html.pipe';

@Component({
  selector: 'app-edit-site',
  imports: [
    CommonModule,
    TranslateModule,
    SmartusComponentsModule,
    FormlyModule,
    OverrideModule,
    SafeHtmlPipe,
    MatDialogModule,
    MtxGridModule,
  ],
  templateUrl: './edit-site.component.html',
  styleUrl: './edit-site.component.scss',
})
export class EditSiteComponent implements OnInit, AfterViewInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private breadcrumbsService = inject(BreadcrumbService);
  private translateService = inject(TranslateService);
  private cd = inject(ChangeDetectorRef);
  private toastr = inject(ToastrService);
  private locationClient = inject(LocationClient);
  private sessionStorageService = inject(SessionStorageService);
  private dialog = inject(MatDialog);
  private userService = inject(UserService);

  @Input() siteId: string;
  @Input() isEmbedded: boolean = false;
  @Output() closeEmbeddedView = new EventEmitter<void>();
  @Output() componentLoaded = new EventEmitter<void>();

  form = new FormGroup({});
  formModel = {};
  formFields: FormlyFieldConfig[] = [];

  canEdit: boolean = false;

  changeReasons: BaseSelectViewModel[] = [];

  siteAddress: AddressViewModel;

  private initialMagentaOfficeValue: boolean;
  isLoading: boolean = false;
  loadingMessage: string = this.translateService.instant(
    'form.loading_message'
  );

  frozenZoneStartDate = '';
  frozenZoneEndDate = '';
  frozenStatus: boolean = false;
  description = '';
  descriptionStyle =
    'background-color: #E20074; color: white; border-radius: 5px; padding: 15px;';
  descriptionContainerStyle =
    'width: 100%; max-width: 840px; border-radius: 15px; padding: 0 15px;';

  cardFields: CardConfig = {
    footer: {
      button2: {
        label: this.translateService.instant('shared.cancel'),
        onClick: () => {
          if (window.opener || window.history.length === 1) {
            window.close();
          } else {
            this.router.navigateByUrl(`/${RoutePath.DATA_MAINTENANCE}`);
          }
        },
        disabled: false,
        type: 'button',
      },
      button1: {
        label: this.translateService.instant('shared.save'),
        onClick: () => {
          this.update();
        },
        disabled: true,
        type: 'submit',
      },
    },
  };

  hasActiveUsages: boolean = false;

  columns: MtxGridColumn[] = [];
  data: RoomViewModel[] = [];
  isLoadingData = false;

  public ngOnInit(): void {
    if (!this.isEmbedded) {
      this.loadingMessage = '';
      this.isLoading = true;
      this.breadcrumbsService.setBreadcrumbs([
        {
          label: this.translateService.instant('edit_site.label__title'),
        },
      ]);
    }

    const hasActiveUsages =
      this.sessionStorageService.get('siteStatus')?.hasActiveUsages;

    if (hasActiveUsages) {
      this.sessionStorageService.set('siteStatus', { hasActiveUsages: false });
    }

    const siteIdSource = this.isEmbedded
      ? of(this.siteId)
      : this.route.paramMap.pipe(map(params => params.get('siteId')!));

    siteIdSource
      .pipe(
        switchMap(siteId => {
          this.siteId = siteId;

          return forkJoin({
            siteData: this.locationClient
              .getArchobjById(this.siteId)
              .pipe(catchError(() => of(null))),
            frozenZoneStatus: this.locationClient
              .checkFrozenZone()
              .pipe(catchError(() => of({ startDate: '', endDate: '' }))),
            changeReasons: this.locationClient.getChangeReasons(),
          }).pipe(
            finalize(() => {
              this.isLoading = false;
              if (this.isEmbedded) {
                setTimeout(() => {
                  this.componentLoaded.emit();
                }, 100);
              }
            })
          );
        })
      )
      .subscribe(results => {
        this.changeReasons = results.changeReasons;
        const aoh = results.siteData;
        this.siteAddress = aoh?.dtArchObjAddress[0]?.address;
        this.initialMagentaOfficeValue = aoh?.dtSite?.isMagentaOfficeSite;
        this.formFields = this.getFormFields();

        this.canEditSite().subscribe(canEdit => {
          this.canEdit = canEdit;
          this.formFields = this.getFormFields();
          this.cd.detectChanges();
        });

        this.loadingMessage = this.translateService.instant(
          'form.loading_message'
        );

        const frozenZoneData = results.frozenZoneStatus;

        if (
          frozenZoneData?.startDate?.length &&
          frozenZoneData?.endDate?.length
        ) {
          this.frozenZoneStartDate = frozenZoneData.startDate;
          this.frozenZoneEndDate = frozenZoneData.endDate;
          this.frozenStatus = true;
          this.description = this.translateService.instant(
            'data_maintenance.frozenZone',
            {
              frozenZoneStartDate: this.frozenZoneStartDate,
              frozenZoneEndDate: this.frozenZoneEndDate,
            }
          );
        } else {
          this.frozenStatus = false;
        }

        if (this.form && this.frozenStatus) {
          this.disableFormFields();
        }
        this.cd.detectChanges();
      });

    this.cd.markForCheck();

    this.columns = [
      {
        header: this.translateService.instant('edit_site.grid.label__spaceId'),
        field: 'roomId',
        sortable: true,
      },
      {
        header: this.translateService.instant('edit_site.grid.label__space'),
        field: 'room',
        sortable: true,
      },
      {
        header: this.translateService.instant(
          'edit_site.grid.label__billingAreaId'
        ),
        field: 'billingAreaId',
        sortable: true,
      },
      {
        header: this.translateService.instant(
          'edit_site.grid.label__billingArea'
        ),
        field: 'billingArea',
        sortable: true,
      },
    ];
  }

  disableFormFields() {
    if (this.frozenStatus && this.form) {
      this.form.disable();
      this.cd.markForCheck();
    }
  }

  ngAfterViewInit() {
    this.form?.valueChanges.subscribe(() => {
      this.checkFormValidity();
    });
  }

  getFormFields(): FormlyFieldConfig[] {
    return [
      {
        type: 'flex-layout',
        fieldGroup: [
          {
            key: 'siteId',
            type: 'input',
            wrappers: ['readOnly'],
            defaultValue: this.siteId,
            props: {
              label: this.translateService.instant('edit_site.label__siteId'),
            },
          },
          {
            type: 'template',
            template: `<p class="text-hint">${this.translateService.instant('edit_site.hint')}</p>`,
          },
          {
            key: 'magentaOffice',
            type: 'checkbox',
            wrappers: ['no-border'],
            defaultValue: this.initialMagentaOfficeValue ?? false,
            props: {
              label: this.translateService.instant(
                'edit_site.label__magentaOffice'
              ),
              disabled: !this.canEdit,
            },
            className: 'margin-to-top',
          },
          {
            key: 'changeReason',
            type: 'input-search',
            props: {
              label: this.translate('buildingMaintenance.table.changeReason'),
              required: true,
              options: this.changeReasons.map(
                (changeReason: BaseSelectViewModel) => ({
                  value: changeReason.id,
                  label: changeReason.name,
                })
              ),
              disabled: !this.canEdit,
            },
          },
        ],
      },
    ];
  }

  checkFormValidity(): void {
    const magentaOfficeChanged =
      this.form.get('magentaOffice')?.value !== this.initialMagentaOfficeValue;
    const changeReasonSelected = !!this.form.get('changeReason')?.value;

    this.updateButtonDisabledState(
      !(magentaOfficeChanged && changeReasonSelected)
    );
  }

  updateButtonDisabledState(isDisabled: boolean) {
    this.cardFields.footer.button1.disabled = isDisabled;
  }

  update() {
    this.isLoading = true;
    const request: UpdateSiteRequest = {
      id: this.siteId,
      changeReason: this.form.get('changeReason').value,
      isMagentaOfficeSite: this.form.get('magentaOffice').value,
    };
    this.locationClient.updateSite(request).subscribe({
      next: res => {
        this.isLoading = false;
        if (res?.roomsToChange?.length > 0) {
          this.sessionStorageService.set('siteStatus', {
            hasActiveUsages: true,
          });
          this.cd.detectChanges();
          const errorDialogRef = this.dialog.open(DialogComponent, {
            panelClass: 'smartportal-dialog',
            width: '500px',
            data: {
              type: 'okButton',
              title: this.translateService.instant(
                'edit_site.dialog.error.title'
              ),
              message: this.translateService.instant(
                'edit_site.dialog.error.message'
              ),
              okButtonText: this.translateService.instant(
                'edit_site.dialog.error.ok_button'
              ),
            },
          });

          errorDialogRef.afterClosed().subscribe({
            next: () => {
              this.data = res.roomsToChange;
              this.hasActiveUsages = true;
              this.cd.detectChanges();
            },
          });
        } else {
          const successDialogRef = this.dialog.open(DialogComponent, {
            panelClass: 'smartportal-dialog',
            width: '500px',
            data: {
              type: 'okButton',
              title: this.translateService.instant(
                'edit_site.dialog.success.title'
              ),
              message: this.translateService.instant(
                'edit_site.dialog.success.message',
                {
                  siteId: this.siteId,
                }
              ),
              okButtonText: this.translateService.instant(
                'ticket.create.success.button__text'
              ),
            },
          });

          successDialogRef.afterClosed().subscribe({
            next: () => {
              this.router.navigateByUrl('datamaintenance');
            },
          });
        }
      },
      error: () => {
        this.isLoading = false;
        this.cd.detectChanges();
        this.toastr.error(
          this.translateService.instant('edit_site.toastr.error')
        );
      },
    });
  }

  translate(key: string): string {
    return this.translateService.instant(key);
  }

  onRowClick(e) {
    this.isLoading = true;
    const archObjId = e.rowData.aoid.substring(0, 5) + 'AD00';
    this.locationClient.getArchobjById(archObjId).subscribe({
      next: res => {
        this.sessionStorageService.set('accountingAreaDefaultValue', res);
        this.sessionStorageService.set(
          'accountingAreaExpandNode',
          e.rowData.roomId
        );
        this.isLoading = false;
        this.cd.detectChanges();
        window.open('/datamaintenance/accounting_areas', '_blank');
      },
    });
  }

  canEditSite(): Observable<boolean> {
    return this.userService.checkIfUserHasRole(
      UserRoles.SMARTPORTAL_EDIT_REM_MO_WE
    );
  }

  get adaptedCardFields(): CardConfig {
    if (this.isEmbedded) {
      return {
        ...this.cardFields,
        footer: {
          ...this.cardFields.footer,
          button2: {
            ...this.cardFields.footer.button2,
            onClick: () => {
              this.closeEmbeddedView.emit();
            },
          },
        },
      };
    }
    return this.cardFields;
  }
}
